<template>
  <div class="duty-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="duty-container">
      <!-- Header Section -->
      <div class="duty-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-clock title-icon"></i>
              Plantões
            </h1>
            <p class="page-subtitle">
              Controle e monitoramento de plantões dos funcionários
            </p>
          </div>

          <div class="actions-section">
            <!-- Espaço para futuras ações se necessário -->
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="duty-content">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-card">
            <div class="filters-grid">
              <!-- User Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-user filter-icon"></i>
                  Usuários
                </label>
                <v-select
                  v-model="user_id"
                  :items="userOptions"
                  outlined
                  dense
                  hide-details
                  class="modern-select"
                  @input="getDuties"
                />
              </div>

              <!-- Start Date Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-calendar-alt filter-icon"></i>
                  Data Início
                </label>
                <v-text-field
                  v-model="date_init"
                  type="date"
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  @input="getDuties"
                />
              </div>

              <!-- End Date Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-calendar-check filter-icon"></i>
                  Data Fim
                </label>
                <v-text-field
                  v-model="date_end"
                  type="date"
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  @input="getDuties"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <ErrorsMessages :errors="errors" />

        <!-- Data Table Section -->
        <div class="table-section">
          <div class="table-card">
            <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
              <v-data-table
                :headers="headers"
                :items="duties"
                item-key="id"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                class="modern-table"
                :no-data-text="'Nenhum plantão encontrado'"
                :loading-text="'Carregando plantões...'"
              >
                <template v-slot:[`item.user_name`]="{ item }">
                  <div class="user-cell">
                    <div class="user-avatar">
                      <img
                        v-if="item.user_photo_url"
                        :src="item.user_photo_url"
                        :alt="item.user_name"
                        class="user-photo"
                      />
                      <i v-else class="fas fa-user"></i>
                    </div>
                    <span class="user-name">{{ item.user_name }}</span>
                  </div>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                  <div class="date-cell">
                    <div class="date-badge start-badge">
                      <i class="fas fa-play mr-1"></i>
                      <span>{{ formatDate(item.created_at, true) }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.updated_at`]="{ item }">
                  <div class="date-cell">
                    <div class="date-badge end-badge">
                      <i class="fas fa-stop mr-1"></i>
                      <span>{{ formatDate(item.updated_at, true) }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.note`]="{ item }">
                  <div class="note-cell">
                    <v-btn
                      v-if="item.note && item.note.trim()"
                      class="note-btn"
                      small
                      @click="openNoteModal(item)"
                    >
                      <i class="fas fa-sticky-note mr-1"></i>
                      Ver Observação
                    </v-btn>
                    <div v-else class="no-note">
                      <i class="fas fa-minus text-muted"></i>
                      <span class="text-muted">Sem observação</span>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.init_location`]="{ item }">
                  <div class="location-cell">
                    <LinkVerLocalizacaoRegistro
                      :item="{
                        id: `${item.id}_init`,
                        latitude: item.init_latitude,
                        longitude: item.init_longitude,
                        user_name: item.user_name,
                        created_at: item.created_at,
                      }"
                      class="location-link start-location"
                    />
                  </div>
                </template>

                <template v-slot:[`item.end_location`]="{ item }">
                  <div class="location-cell">
                    <LinkVerLocalizacaoRegistro
                      :item="{
                        id: `${item.id}_end`,
                        latitude: item.end_latitude,
                        longitude: item.end_longitude,
                        user_name: item.user_name,
                        created_at: item.created_at,
                      }"
                      class="location-link end-location"
                    />
                  </div>
                </template>

                <template v-slot:[`item.duration`]="{ item }">
                  <div class="duration-cell">
                    <div class="duration-badge">
                      <i class="fas fa-hourglass-half mr-1"></i>
                      <span>{{
                        calculateDuration(item.created_at, item.updated_at)
                      }}</span>
                    </div>
                  </div>
                </template>
              </v-data-table>
            </b-overlay>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Observação -->
    <b-modal id="noteModal" size="lg" hide-footer modal-class="modern-modal">
      <template v-slot:modal-title>
        <div class="modal-title-modern">
          <i class="fas fa-sticky-note mr-2"></i>
          Observação do Plantão
        </div>
      </template>

      <div class="modal-content-modern" v-if="selectedDuty">
        <!-- Informações do Plantão -->
        <div class="duty-info-section">
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">Usuário</label>
              <div class="info-content">
                <div class="user-display">
                  <div class="user-avatar-modal">
                    <img
                      v-if="selectedDuty.user_photo_url"
                      :src="selectedDuty.user_photo_url"
                      :alt="selectedDuty.user_name"
                      class="user-photo-modal"
                    />
                    <i v-else class="fas fa-user"></i>
                  </div>
                  <span class="user-name-modal">{{
                    selectedDuty.user_name
                  }}</span>
                </div>
              </div>
            </div>

            <div class="info-item">
              <label class="info-label">Período do Plantão</label>
              <div class="info-content">
                <div class="period-display">
                  <div class="period-item">
                    <i class="fas fa-play period-icon start-icon"></i>
                    <span>{{ formatDate(selectedDuty.created_at, true) }}</span>
                  </div>
                  <div class="period-separator">
                    <i class="fas fa-arrow-right"></i>
                  </div>
                  <div class="period-item">
                    <i class="fas fa-stop period-icon end-icon"></i>
                    <span>{{ formatDate(selectedDuty.updated_at, true) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-item">
              <label class="info-label">Duração Total</label>
              <div class="info-content">
                <div class="duration-display">
                  <i class="fas fa-hourglass-half mr-2"></i>
                  <span class="duration-text">{{
                    calculateDuration(
                      selectedDuty.created_at,
                      selectedDuty.updated_at
                    )
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Observação -->
        <div class="note-section">
          <div class="note-header">
            <h4 class="note-title">
              <i class="fas fa-comment-alt mr-2"></i>
              Observação
            </h4>
          </div>
          <div class="note-content-display">
            <div class="note-text-area">
              {{ selectedDuty.note }}
            </div>
          </div>
        </div>

        <!-- Ações do Modal -->
        <div class="modal-actions">
          <v-btn class="btn-close-modal" @click="$bvModal.hide('noteModal')">
            <i class="fas fa-times mr-2"></i>
            Fechar
          </v-btn>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import { formatDate } from "../lib/format";
import LinkVerLocalizacaoRegistro from "../templates/Components/LinkVerLocalizacaoRegistro";
import NavBar from "../templates/NavBar";
import routes from "../routes";
import api from "../services/api";
import moment from "moment";

export default {
  name: "Duty",

  components: {
    NavBar,
    AuthBySessionExpired,
    LinkVerLocalizacaoRegistro,
    ErrorsMessages,
  },

  data() {
    return {
      user_id: "",
      users: [],
      duties: [],
      date_init: "",
      date_end: "",
      errors: [],
      loading: false,
      selectedDuty: null,
      headers: [
        {
          text: "USUÁRIO",
          value: "user_name",
          sortable: false,
          width: "200px",
        },
        {
          text: "INÍCIO DO PLANTÃO",
          value: "created_at",
          sortable: false,
          width: "180px",
        },
        {
          text: "FIM DO PLANTÃO",
          value: "updated_at",
          sortable: false,
          width: "180px",
        },
        {
          text: "DURAÇÃO",
          value: "duration",
          sortable: false,
          width: "120px",
        },
        {
          text: "OBSERVAÇÃO",
          value: "note",
          sortable: false,
          width: "150px",
        },
        {
          text: "LOCAL INÍCIO",
          value: "init_location",
          sortable: false,
          width: "120px",
        },
        {
          text: "LOCAL FIM",
          value: "end_location",
          sortable: false,
          width: "120px",
        },
      ],
    };
  },

  computed: {
    userOptions() {
      return [
        { text: "Todos os usuários", value: "" },
        ...this.users.map((user) => ({
          text: user.name,
          value: user.id,
        })),
      ];
    },
  },

  mounted() {
    this.getUsers();
    this.getDuties();
  },

  methods: {
    formatDate,

    handleToBack: () => routes.go(-1),

    openNoteModal(duty) {
      this.selectedDuty = duty;
      this.$bvModal.show("noteModal");
    },

    calculateDuration(startDate, endDate) {
      if (!startDate || !endDate) return "N/A";

      const start = moment(startDate);
      const end = moment(endDate);
      const duration = moment.duration(end.diff(start));

      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      return `${hours}h ${minutes}m ${seconds}s`;
    },

    async getUsers() {
      try {
        this.loading = true;

        const response = await api.get("/rest-home/duty-control/user-duty");

        const { error, users } = response.data;

        if (!error) {
          this.users = users;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async getDuties() {
      try {
        this.loading = true;

        const { user_id, date_init, date_end } = this;

        let params = { user_id, date_init, date_end };

        const response = await api.get("/rest-home/duty-control/duties", {
          params,
        });

        const { error, errors, duties } = response.data;

        if (error) {
          this.errors = errors;
          this.duties = [];
        } else {
          this.errors = [];
          this.duties = duties;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.duty-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.duty-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.duty-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* Buttons */
.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Content Section */
.duty-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

/* Table Section */
.table-section {
  margin-bottom: 32px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.loading-overlay {
  border-radius: 16px;
}

/* Table Cell Styling */
.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.user-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  transition: transform 0.2s ease;
}

.user-name {
  font-weight: 500;
  color: #1e293b;
}

.date-cell {
  display: flex;
  align-items: center;
}

.date-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.start-badge {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.end-badge {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.duration-cell {
  display: flex;
  align-items: center;
}

.duration-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
  border: 1px solid #818cf8;
}

.note-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.note-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
}

.note-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4) !important;
}

.no-note {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}

.location-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modal Styling */
.modal-title-modern {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.modal-content-modern {
  padding: 8px 0;
}

/* Duty Info Section */
.duty-info-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item {
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  display: block;
}

.info-content {
  font-size: 14px;
  color: #1e293b;
}

.user-display {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar-modal {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
}

.user-photo-modal {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.user-name-modal {
  font-weight: 600;
  color: #1e293b;
}

.period-display {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.period-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f1f5f9;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.period-icon {
  font-size: 12px;
}

.start-icon {
  color: #059669;
}

.end-icon {
  color: #dc2626;
}

.period-separator {
  color: #6b7280;
  font-size: 12px;
}

.duration-display {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  border-radius: 20px;
  color: #4338ca;
  font-weight: 600;
  width: fit-content;
}

.duration-text {
  font-size: 14px;
}

/* Note Section */
.note-section {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  margin-bottom: 24px;
}

.note-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.note-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.note-title i {
  color: #0f65b0;
}

.note-content-display {
  padding: 20px;
}

.note-text-area {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  color: #374151;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.btn-close-modal {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  padding: 10px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.btn-close-modal:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  color: #374151 !important;
}

/* Table Styling Override */
.modern-table {
  background: transparent !important;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
  padding: 16px 12px !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modern-table >>> .v-data-table tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

/* Input and Select Override */
.modern-input >>> .v-input__control,
.modern-select >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-input >>> .v-text-field__slot input,
.modern-select >>> .v-select__selection {
  font-weight: 500 !important;
}

/* Modal Override */
.modern-modal >>> .modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-radius: 0 !important;
}

.modern-modal >>> .modal-body {
  padding: 24px !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }

  .filters-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .period-display {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .period-item {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .duty-container {
    padding: 0 16px;
  }

  .duty-header {
    padding: 16px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .filters-card,
  .table-card {
    border-radius: 12px;
    padding: 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* Mobile table responsiveness */
  .modern-table >>> .v-data-table__wrapper {
    overflow-x: auto;
  }

  .user-cell {
    min-width: 140px;
  }

  .date-badge,
  .duration-badge {
    font-size: 10px;
    padding: 4px 8px;
  }

  .note-cell {
    max-width: 200px;
  }
}

@media (max-width: 480px) {
  .btn-back-modern {
    padding: 10px 16px !important;
    font-size: 14px !important;
  }

  .page-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .user-avatar {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }

  .date-badge,
  .duration-badge {
    font-size: 9px;
    padding: 3px 6px;
  }
}
</style>